import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Input } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { ExtractionJobDTO } from "@wmart/shared-types";

type Mode = "url" | "file";

export function NewExtraction() {
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();

  const [mode, setMode] = useState<Mode>("url");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === "url" && url.trim() === "") {
      notify("error", "Enter a supplier URL");
      return;
    }
    if (mode === "file" && !file) {
      notify("error", "Choose a file to upload");
      return;
    }
    setBusy(true);
    try {
      if (mode === "url") {
        const job = await api.post<ExtractionJobDTO>(
          "/v1/admin/catalog/extractions",
          { source_type: "url", source_ref: url },
        );
        notify("success", "Extraction job queued");
        navigate(`/catalog/extractions/${job.id}`);
      } else {
        if (!file) return;
        // Direct-to-GCS upload (P22 large-PDF path): bypasses Cloud Run's
        // 32 MB ingress + the api's multipart parser. Three steps:
        //  1) ask api for a V4 signed PUT URL bound to the file's mime;
        //  2) PUT the bytes straight to GCS;
        //  3) create the extraction job with the returned object key.
        const contentType = file.type || "application/octet-stream";
        const { key, upload_url } = await api.post<{
          key: string;
          upload_url: string;
          expires_at: string;
        }>("/v1/admin/catalog/extractions/upload-url", {
          content_type: contentType,
        });
        const putRes = await fetch(upload_url, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: file,
        });
        if (!putRes.ok) {
          throw new Error(
            `Upload to storage failed (${putRes.status} ${putRes.statusText})`,
          );
        }
        const job = await api.post<ExtractionJobDTO>(
          "/v1/admin/catalog/extractions",
          { source_type: "file", source_ref: key },
        );
        notify("success", "Extraction job queued");
        navigate(`/catalog/extractions/${job.id}`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to start extraction");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-xl flex-col gap-6">
      <h1 className="font-serif text-display-sm text-ink-900">
        New extraction
      </h1>

      <Card className="p-6">
        {/* Mode toggle */}
        <div className="mb-6 flex gap-2">
          <Button
            variant={mode === "url" ? "primary" : "secondary"}
            size="sm"
            onClick={() => setMode("url")}
            type="button"
          >
            From URL
          </Button>
          <Button
            variant={mode === "file" ? "primary" : "secondary"}
            size="sm"
            onClick={() => setMode("file")}
            type="button"
          >
            Upload file
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {mode === "url" ? (
            <Input
              type="url"
              label="Supplier catalog URL"
              placeholder="https://supplier.example.com/catalog.pdf"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
          ) : (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="extraction-file"
                className="text-body-sm font-medium text-ink-700"
              >
                Supplier file (PDF or image)
              </label>
              <input
                id="extraction-file"
                data-testid="file-input"
                type="file"
                accept=".pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                }}
                className="text-body-sm text-ink-700 file:mr-3 file:rounded-sm file:border file:border-ink-200 file:bg-white file:px-3 file:py-1.5 file:text-body-sm hover:file:bg-accent-soft"
              />
            </div>
          )}

          <div>
            <Button type="submit" disabled={busy} loading={busy}>
              Submit
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
